import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const { inviteToken } = useParams();
  const [inviteData, setInviteData] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [researchConsent, setResearchConsent] = useState(false);
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const { register } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/validate-invite/${inviteToken}`);
        setInviteData(res.data);
      } catch {
        setError('Invalid or expired invitation link');
      } finally {
        setValidating(false);
      }
    })();
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) return setError('Passwords do not match');
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (!policyAcknowledged) return setError('Please acknowledge the authenticity-analysis policy to continue.');
    setLoading(true);
    try {
      const user = await register(inviteToken, {
        name, email, password,
        research_consent: researchConsent,
        policy_acknowledged: policyAcknowledged,
      });
      const map = { student: '/student', teacher: '/teacher', developer: '/developer' };
      navigate(map[user.role] || '/student');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2"
               style={{ borderColor: 'var(--unthsc-green)' }}></div>
          <p className="mt-3 text-gray-600">Validating invitation…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden md:flex md:w-1/2 flex-col justify-between p-12 text-white"
           style={{ background: 'var(--ai-hero-gradient)' }}>
        <div className="flex items-center gap-4 self-start">
          <img
            src="/branding/ai-tutor-hero.png"
            alt={brand.product_name}
            className="h-24 w-24 rounded-xl object-contain ring-1 ring-white/15"
          />
          <div className="leading-tight">
            <div className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: 'var(--gr-bone)' }}>
              IoA
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] mt-1" style={{ color: 'var(--gr-gold)' }}>
              Institute of Analytics
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest opacity-80 mb-2">{brand.course_title} — {brand.institution_short_name}</div>
          <h2 className="text-4xl font-bold leading-tight">{brand.course_subtitle}</h2>
          <p className="mt-3 text-white/90 max-w-md">Create your account using the invitation provided by your administrator.</p>
        </div>
        <div className="text-xs opacity-75">© {brand.parent_org_name}</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold mb-1" style={{ color: 'var(--unthsc-green)' }}>Create account</h1>
          {inviteData && (
            <p className="text-sm text-gray-500 mb-8">
              Registering as <span className="badge-role" data-testid="invite-role-badge">{inviteData.role}</span>
            </p>
          )}

          {error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-testid="register-error">
              {error}
              <div className="mt-3">
                <a href="/login" className="font-semibold underline" style={{ color: 'var(--unthsc-green)' }}>Go to Login</a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="register-form">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     className="input-field mb-4" required data-testid="register-name-input" />

              <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     className="input-field mb-4" required data-testid="register-email-input" />

              <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     className="input-field mb-4" required minLength={6} data-testid="register-password-input" />

              <label className="block text-sm font-semibold text-gray-700 mb-1">Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                     className="input-field mb-5" required minLength={6} data-testid="register-confirm-input" />

              {/* Consent checkboxes — 2026-06-07 */}
              <div className="mb-5 space-y-3 p-4 rounded" style={{ background: 'var(--gr-bone-2)', border: '1px solid var(--gr-border)' }}>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="register-policy-row">
                  <input
                    type="checkbox"
                    checked={policyAcknowledged}
                    onChange={(e) => setPolicyAcknowledged(e.target.checked)}
                    className="mt-1"
                    required
                    data-testid="register-policy-checkbox"
                  />
                  <span style={{ color: 'var(--gr-ink)' }}>
                    <b>Required.</b> I acknowledge my submissions may be analyzed for authenticity
                    (paste-ratio, revision patterns, AI-write signals) to support faculty judgement.
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="register-research-row">
                  <input
                    type="checkbox"
                    checked={researchConsent}
                    onChange={(e) => setResearchConsent(e.target.checked)}
                    className="mt-1"
                    data-testid="register-research-checkbox"
                  />
                  <span style={{ color: 'var(--gr-ink)' }}>
                    <i>Optional.</i> I consent to my anonymized tutor usage data being used for
                    research and service improvement. You can revoke this at any time from your profile.
                  </span>
                </label>
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="register-submit-btn">
                {loading ? 'Creating Account…' : 'Create Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
